import createClient from "openapi-fetch";

import type { paths } from "./generated/schema";
import type { TokenPair, TokenStore } from "./tokens";

export interface CreateApiOptions {
  /** API 根路徑，例如 http://localhost:8000/api/v1 */
  baseUrl: string;
  tokens: TokenStore;
  /**
   * refresh 也失敗時呼叫（refresh token 過期或已被列入黑名單）。
   * 前端應在這裡清掉狀態並導向登入頁。
   */
  onUnauthorized?: () => void;
  /** 覆寫底層的 fetch，測試時很有用 */
  fetchImpl?: typeof fetch;
}

const REFRESH_PATH = "/auth/refresh/";
const LOGIN_PATH = "/auth/login/";

/**
 * 建立一個型別安全的 API client。
 *
 * 路徑、查詢參數、request body 與 response 的型別全部來自
 * `schema/openapi.yaml`（由 Django 產生），所以後端改了欄位而前端
 * 沒有跟上時，TypeScript 會在編譯期就報錯。
 *
 * 自動處理 access token 過期：
 *   1. 每個請求自動帶上 Authorization: Bearer <access>
 *   2. 收到 401 時用 refresh token 換新的一組 token，然後重試一次
 *   3. 同時有多個請求撞到 401 時只會換一次（single-flight）
 */
export function createApi({ baseUrl, tokens, onUnauthorized, fetchImpl }: CreateApiOptions) {
  const baseFetch: typeof fetch = fetchImpl ?? ((...args) => globalThis.fetch(...args));

  // 同一時間只允許一個 refresh 請求在飛
  let refreshing: Promise<TokenPair | null> | null = null;

  async function refreshTokens(): Promise<TokenPair | null> {
    const refresh = await tokens.getRefresh();
    if (!refresh) return null;

    const response = await baseFetch(`${baseUrl}${REFRESH_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    });

    if (!response.ok) return null;

    const data = (await response.json()) as Partial<TokenPair>;
    if (!data.access) return null;

    // 後端開啟了 refresh token 輪替，所以回傳的新 refresh 一定要存回去
    const pair: TokenPair = { access: data.access, refresh: data.refresh ?? refresh };
    await tokens.setTokens(pair);
    return pair;
  }

  const authFetch: typeof fetch = async (input, init) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    // 登入與更新 token 本身不需要（也不該）帶舊的 access token
    const isAuthEndpoint = url.endsWith(LOGIN_PATH) || url.endsWith(REFRESH_PATH);

    /**
     * 送出一次請求。
     *
     * 兩個容易踩的點（都是實際踩過才發現的）：
     *
     * 1. openapi-fetch 是以 `fetch(new Request(...))` 的形式呼叫，標頭
     *    （含 Content-Type: application/json）在 Request 物件上而不在 init 裡。
     *    依規格，`fetch(input, init)` 在 init.headers 存在時會**取代**而非合併
     *    Request 上的標頭 —— 所以若只從 init 取標頭再傳 `{ ...init, headers }`，
     *    Content-Type 會消失、被推斷成 text/plain，後端回 415。
     *    所有 POST/PATCH 都會壞，而 TypeScript 檢查不出來。
     *
     * 2. Request 的 body 只能讀一次。401 重試時必須用 clone()，
     *    否則第二次會拋 "body already used"。
     */
    const attempt = async (accessToken: string | null): Promise<Response> => {
      const isRequest = input instanceof Request;
      const headers = new Headers(isRequest ? input.headers : undefined);
      // init 上的標頭優先（呼叫端明確指定的）
      new Headers(init?.headers).forEach((value, key) => headers.set(key, value));

      if (accessToken && !isAuthEndpoint) {
        headers.set("Authorization", `Bearer ${accessToken}`);
      }

      const target = isRequest ? input.clone() : input;
      return baseFetch(target, { ...init, headers });
    };

    const access = await tokens.getAccess();
    const response = await attempt(access);

    if (response.status !== 401 || isAuthEndpoint) {
      return response;
    }

    refreshing ??= refreshTokens().finally(() => {
      refreshing = null;
    });
    const pair = await refreshing;

    if (!pair) {
      await tokens.clear();
      onUnauthorized?.();
      return response;
    }

    return attempt(pair.access);
  };

  return createClient<paths>({ baseUrl, fetch: authFetch });
}

export type ApiClient = ReturnType<typeof createApi>;
